import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaFilePdf, FaTrash, FaEye, FaDownload, FaChevronRight, FaPencilAlt, FaSave, FaTimes } from 'react-icons/fa';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../components/firebase';
import { toast } from 'react-toastify';
import { useTheme } from '../../../context/ThemeContext';

const PdfCards = ({
  notes,
  category,
  onOpenModal,
  onDeleteNote,
  onRenameNote,
  isAdmin,
  renderEmptyState,
  isLoading: parentLoading
}) => {
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState({});
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { isDarkMode } = useTheme();

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    surface: 'var(--bg-surface)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
    glass: 'var(--glass)',
    glassBorder: 'var(--glass-border)'
  };

  // Helper function to initialize or update stats document
  const initOrUpdateStats = async (noteId, updateData = {}) => {
    const docRef = doc(db, 'pdfStats', noteId);
    try {
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Create new document with initial values
        const initialData = {
          views: 0,
          downloads: 0,
          lastViewed: null,
          lastDownloaded: null,
          ...updateData
        };
        await setDoc(docRef, initialData);
        return initialData;
      } else if (Object.keys(updateData).length > 0) {
        // Update existing document
        await updateDoc(docRef, updateData);
        const updatedDoc = await getDoc(docRef);
        return updatedDoc.data();
      }

      return docSnap.data();
    } catch (error) {
      console.error('Error handling stats document:', error);
      return { views: 0, downloads: 0 };
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      const statsData = {};
      const relevantNotes = notes.filter(n => n.category.toLowerCase() === category.toLowerCase());
      for (const note of relevantNotes) {
        statsData[note.id] = await initOrUpdateStats(note.id);
      }
      setStats(statsData);
    };
    loadStats();
  }, [notes, category]);

  // Track view count (separate function for reusability)
  const trackView = async (noteId) => {
    try {
      await initOrUpdateStats(noteId, {
        views: increment(1),
        lastViewed: new Date()
      });

      setStats(prev => ({
        ...prev,
        [noteId]: {
          ...prev[noteId],
          views: (prev[noteId]?.views || 0) + 1
        }
      }));
      return true;
    } catch (error) {
      console.error('Error tracking view:', error);
      return false;
    }
  };

  // Track card view when clicked
  const handleCardClick = async (noteId, index) => {
    if (editingNoteId === noteId) return; // don't open modal while editing
    await trackView(noteId);
    onOpenModal(category, index);
  };

  const startEditing = (e, note) => {
    e.stopPropagation();
    setEditingNoteId(note.id);
    setEditingTitle(note.title);
  };

  const cancelEditing = (e) => {
    e && e.stopPropagation();
    setEditingNoteId(null);
    setEditingTitle('');
  };

  const saveRename = async (e, note) => {
    e.stopPropagation();
    if (!editingTitle.trim()) {
      toast.warning('Title cannot be empty');
      return;
    }
    if (editingTitle.trim() === note.title) {
      cancelEditing();
      return;
    }
    setIsSaving(true);
    try {
      await updateDoc(doc(db, note._collection || category, note.id), { title: editingTitle.trim() });
      if (onRenameNote) onRenameNote(note.id, editingTitle.trim());
      toast.success('PDF name updated!');
      setEditingNoteId(null);
    } catch (err) {
      console.error('Rename error:', err);
      toast.error('Failed to rename. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async (note) => {
    setIsLoading(prev => ({ ...prev, [note.id]: true }));
    try {
      // First track a view (since downloading implies viewing)
      await trackView(note.id);

      // Then update download count
      const updatedStats = await initOrUpdateStats(note.id, {
        downloads: increment(1),
        lastDownloaded: new Date()
      });

      // Update local state with fresh data
      setStats(prev => ({
        ...prev,
        [note.id]: {
          ...updatedStats,
          views: prev[note.id]?.views || updatedStats.views || 0,
          downloads: updatedStats.downloads || (prev[note.id]?.downloads || 0) + 1
        }
      }));

      // Trigger download
      const fileId = extractFileId(note.link);
      if (fileId) {
        const downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${note.title}.pdf` || 'download.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Download started!');
      }
    } catch (error) {
      console.error('Error handling download:', error);
      toast.error('Failed to initiate download');
    } finally {
      setIsLoading(prev => ({ ...prev, [note.id]: false }));
    }
  };

  const extractFileId = (url) => {
    const patterns = [
      /\/file\/d\/([^\/]+)/,
      /\/d\/([^\/]+)/,
      /id=([^&]+)/,
      /[-\w]{25,}/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const filteredNotes = notes.filter((note) => note.category.toLowerCase() === category.toLowerCase());

  if (filteredNotes.length === 0 && !parentLoading) {
    return renderEmptyState(category);
  }

  return (
    <div className="flex flex-col gap-4 mt-6">
      {parentLoading ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl border" style={{ backgroundColor: c.surface, borderColor: c.glassBorder }}></div>
          ))}
        </div>
      ) : (
        filteredNotes.map((note, index) => (
          <motion.div
            key={note.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="group relative"
          >
            {/* Inline Edit Mode */}
            {isAdmin && editingNoteId === note.id ? (
              <div
                className="flex items-center gap-3 p-4 md:p-5 border rounded-2xl"
                style={{ backgroundColor: c.glass, borderColor: 'var(--accent)' }}
                onClick={e => e.stopPropagation()}
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-xl flex items-center justify-center border" style={{ backgroundColor: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)' }}>
                  <FaPencilAlt size={18} style={{ color: 'var(--accent)' }} />
                </div>
                <input
                  autoFocus
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveRename(e, note);
                    if (e.key === 'Escape') cancelEditing();
                  }}
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold outline-none"
                  style={{ background: 'var(--bg-surface)', border: '1px solid var(--accent)', color: 'var(--text-main)' }}
                  placeholder="Enter new PDF name..."
                />
                <motion.button
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={e => saveRename(e, note)}
                  disabled={isSaving}
                  className="p-3 rounded-xl border text-white"
                  style={{ background: 'var(--accent)', borderColor: 'var(--accent)' }}
                  title="Save name"
                >
                  {isSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaSave size={15} />}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={cancelEditing}
                  className="p-3 rounded-xl border"
                  style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.muted }}
                  title="Cancel"
                >
                  <FaTimes size={15} />
                </motion.button>
              </div>
            ) : (
              <div
                onClick={() => handleCardClick(note.id, index)}
                className="flex items-center gap-3 md:gap-4 p-3 md:p-4 border rounded-2xl transition-all cursor-pointer"
                style={{ backgroundColor: c.glass, borderColor: c.glassBorder }}
              >
                {/* Specialized Icon Box */}
                <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-xl flex items-center justify-center border transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'rgba(var(--accent-rgb), 0.1)', borderColor: 'rgba(var(--accent-rgb), 0.2)' }}>
                  <FaFilePdf size={24} style={{ color: 'var(--accent)' }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base md:text-xl truncate mb-0.5 md:mb-1" style={{ color: c.text }}>
                    {note.title}
                  </h3>
                  <div className="flex items-center gap-4 text-xs md:text-sm" style={{ color: c.muted }}>
                    <div className="flex items-center gap-1.5">
                      <FaEye className="opacity-60" />
                      <span>{stats[note.id]?.views || 0} views</span>
                    </div>
                    <div className="flex items-center gap-1.5 border-l pl-3 md:pl-4" style={{ borderColor: c.glassBorder }}>
                      <FaDownload className="opacity-60" />
                      <span>{stats[note.id]?.downloads || 0}</span>
                    </div>
                    <div className="hidden sm:block border-l pl-4" style={{ borderColor: c.glassBorder }}>
                      <span className="px-2 py-0.5 rounded uppercase tracking-wider text-[10px]" style={{ backgroundColor: c.surface, color: c.muted, border: `1px solid ${c.glassBorder}` }}>PDF Document</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 md:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(note);
                    }}
                    disabled={isLoading[note.id]}
                    className="p-3 rounded-xl transition-all border"
                    style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.muted }}
                    title="Download"
                  >
                    <FaDownload size={14} />
                  </motion.button>

                  {isAdmin && (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => startEditing(e, note)}
                        className="p-3 rounded-xl transition-all border"
                        style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: 'var(--accent)' }}
                        title="Rename PDF"
                      >
                        <FaPencilAlt size={13} />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteNote(note);
                        }}
                        className="p-3 rounded-xl transition-all border text-red-400 hover:bg-red-500 hover:text-white"
                        style={{ backgroundColor: c.surface, borderColor: c.glassBorder }}
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </motion.button>
                    </>
                  )}

                  <div className="hidden md:flex ml-2 w-8 h-8 md:w-10 md:h-10 rounded-full items-center justify-center transition-all group-hover:bg-[var(--accent)] group-hover:text-white" style={{ backgroundColor: c.surface, color: c.muted }}>
                    <FaChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))
      )}
    </div>
  );
};

export default PdfCards;